// src/pages/billing/TrialBalancePage.tsx

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { observer } from '@react';
import { useBillingReport } from '@react/hooks';
import {
  Button,
  Input,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  Badge,
  ErrorAlert,
} from '@react/components';

function formatMoney(money: any): string {
  if (!money) return '--';
  const amount = (money.amount ?? 0) / Math.pow(10, money.decimal_places ?? 2);
  return amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function enumLabel(value: string, prefix: string): string {
  return (value || '').replace(prefix, '').replace(/_/g, ' ');
}

export const TrialBalancePage = observer(() => {
  const store = useBillingReport();
  const [asOfDate, setAsOfDate] = useState(new Date().toISOString().split('T')[0]);
  const [report, setReport] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  const loadReport = async () => {
    setLoading(true);
    try {
      const res = await store.GetTrialBalance({
        as_of: new Date(asOfDate).toISOString(),
      }) as any;
      setReport(res?.trial_balance ?? res);
    } catch {
      // Error handled by store
    } finally {
      setLoading(false);
    }
  };

  const isBalanced = report && Math.abs((report.difference?.amount ?? 0)) < 1;

  return (
    <div className="space-y-6">
      {/* The tab already names this page; the heading stays for screen readers. */}
      <div>
        <h1 className="sr-only">Trial Balance</h1>
        <nav className="text-sm">
          <Link to="/billing" className="text-muted-foreground hover:text-foreground">Billing</Link>
          <span className="mx-2 text-muted-foreground">/</span>
          <span className="text-foreground">Trial Balance</span>
        </nav>
      </div>

      {store.error ? <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} /> : null}

      <Card>
        <CardContent className="p-4">
          <div className="flex gap-4 items-end">
            <div className="w-64">
              <Input label="As of Date" type="date" value={asOfDate} onChange={(e) => setAsOfDate(e.target.value)} />
            </div>
            <Button onClick={loadReport} disabled={loading}>
              {loading ? 'Generating...' : 'Generate Report'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {report && (
        <>
          {/* Summary */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Card>
              <CardContent className="p-5">
                <p className="text-sm text-muted-foreground">Total Debits</p>
                <p className="text-2xl font-bold text-foreground">{formatMoney(report.total_debits)}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-5">
                <p className="text-sm text-muted-foreground">Total Credits</p>
                <p className="text-2xl font-bold text-foreground">{formatMoney(report.total_credits)}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-5">
                <p className="text-sm text-muted-foreground">Difference</p>
                <div className="flex items-center gap-2">
                  <p className="text-2xl font-bold text-foreground">{formatMoney(report.difference)}</p>
                  {isBalanced ? (
                    <Badge variant="success" size="sm">Balanced</Badge>
                  ) : (
                    <Badge variant="error" size="sm">Unbalanced</Badge>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Detail table */}
          <Card>
            <CardHeader>
              <CardTitle>Account Balances</CardTitle>
              <CardDescription>As of {asOfDate}</CardDescription>
            </CardHeader>
            <CardContent>
              {(report.lines?.lines ?? []).length > 0 ? (
                <Table striped hoverable>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Code</TableHead>
                      <TableHead>Account Name</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead className="text-right">Debit Balance</TableHead>
                      <TableHead className="text-right">Credit Balance</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(report.lines.lines).map((line: any, i: number) => (
                      <TableRow key={line.account?.id || i}>
                        <TableCell className="font-mono">{String(line.account_code ?? '--')}</TableCell>
                        <TableCell>{String(line.account_name ?? '--')}</TableCell>
                        <TableCell>{enumLabel(line.account_type, 'ACCOUNT_TYPE_')}</TableCell>
                        <TableCell className="text-right">{line.debit_balance?.amount ? formatMoney(line.debit_balance) : ''}</TableCell>
                        <TableCell className="text-right">{line.credit_balance?.amount ? formatMoney(line.credit_balance) : ''}</TableCell>
                      </TableRow>
                    ))}
                    <TableRow className="font-bold border-t-2">
                      <TableCell colSpan={3} className="text-right">Total</TableCell>
                      <TableCell className="text-right">{formatMoney(report.total_debits)}</TableCell>
                      <TableCell className="text-right">{formatMoney(report.total_credits)}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              ) : (
                <p className="text-sm text-muted-foreground">No account balances found</p>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
});

export default TrialBalancePage;
